import { useMemo, useState } from 'react';
import {
  formatMoney,
  fromLocalInputValue,
  parseAmountToSen,
  toLocalInputValue,
  type Transaction,
} from '@wis/shared';
import { useApp } from '../state/AppContext.js';
import { confirmTransaction, deleteTransaction, restoreTransaction } from '../db/repository.js';
import {
  Button,
  EmptyState,
  Field,
  Notice,
  Select,
  TextInput,
  sourceLabel,
} from '../components/primitives.js';

/**
 * The review inbox.
 *
 * Anything the app was not sure about lands here: a Shortcut capture with no
 * merchant, a receipt whose date could not be read, a CSV row with a guessed
 * category. Nothing here is counted as wrong -- it is counted, and flagged, and
 * the user gets to say yes or fix it in a few taps.
 */
export function Review(): JSX.Element {
  const { transactions, categories, accounts, settings, toast } = useApp();

  const queue = useMemo(
    () =>
      transactions
        .filter((transaction) => transaction.reviewStatus === 'needs-review')
        .sort(
          (a, b) =>
            a.importConfidence - b.importConfidence || b.occurredAt.localeCompare(a.occurredAt),
        ),
    [transactions],
  );

  if (queue.length === 0) {
    return (
      <div style={{ marginTop: 'var(--space-6)' }}>
        <EmptyState
          glyph="✓"
          title="Nothing to review"
          body="Anything captured by a Shortcut or read from a receipt that the app was unsure about will appear here, with the uncertain parts highlighted."
        />
      </div>
    );
  }

  return (
    <div className="stack-lg" style={{ marginTop: 'var(--space-3)' }}>
      <Notice tone="info">
        {queue.length} {queue.length === 1 ? 'record needs' : 'records need'} a look. These are
        already counted in your totals -- confirming just marks them as checked.
      </Notice>

      {queue.map((transaction) => (
        <ReviewCard
          key={transaction.id}
          transaction={transaction}
          categories={categories}
          accounts={accounts}
          timeZone={settings.timeZone}
          onConfirmed={(message) => toast(message)}
          onDeleted={(id) =>
            toast('Deleted', {
              label: 'Undo',
              onAction: () => {
                void restoreTransaction(id);
              },
            })
          }
        />
      ))}
    </div>
  );
}

interface ReviewCardProps {
  transaction: Transaction;
  categories: ReturnType<typeof useApp>['categories'];
  accounts: ReturnType<typeof useApp>['accounts'];
  timeZone: string;
  onConfirmed: (message: string) => void;
  onDeleted: (id: string) => void;
}

function ReviewCard({
  transaction,
  categories,
  accounts,
  timeZone,
  onConfirmed,
  onDeleted,
}: ReviewCardProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [amountText, setAmountText] = useState((transaction.amountSen / 100).toFixed(2));
  const [merchant, setMerchant] = useState(
    transaction.merchantNormalized || transaction.merchantOriginal,
  );
  const [categoryId, setCategoryId] = useState(transaction.categoryId);
  const [accountLabel, setAccountLabel] = useState(transaction.accountLabel);
  const [occurredAtLocal, setOccurredAtLocal] = useState(
    toLocalInputValue(transaction.occurredAt, timeZone),
  );
  const [error, setError] = useState<string | undefined>();

  const confidence = Math.round(transaction.importConfidence * 100);

  // Which fields to point at, derived from what a capture of this kind is
  // usually missing rather than from a stored flag.
  const uncertain = [
    merchant ? null : 'merchant',
    transaction.importConfidence < 0.7 ? 'category' : null,
  ].filter(Boolean) as string[];

  async function handleConfirm(withEdits: boolean): Promise<void> {
    setError(undefined);

    if (!withEdits) {
      await confirmTransaction(transaction.id);
      onConfirmed('Confirmed');
      return;
    }

    let amountSen: number;
    try {
      amountSen = Math.abs(parseAmountToSen(amountText));
    } catch {
      setError('Enter an amount like 12.90');
      return;
    }
    if (amountSen === 0) {
      setError('Enter an amount greater than zero.');
      return;
    }

    let occurredAt: string;
    try {
      occurredAt = fromLocalInputValue(occurredAtLocal, timeZone);
    } catch {
      setError('That date and time could not be read.');
      return;
    }

    await confirmTransaction(transaction.id, {
      amountSen,
      merchant,
      categoryId,
      accountLabel,
      occurredAt,
    });
    onConfirmed('Saved and confirmed');
  }

  return (
    <section className="card" aria-label={`Review ${merchant || 'untitled payment'}`}>
      <div className="row row--between">
        <div className="grow">
          <p className="money money--large">
            {formatMoney(transaction.amountSen, { currency: transaction.currency })}
          </p>
          <p className="small muted">
            {merchant || <em>No merchant was captured</em>} ·{' '}
            {toLocalInputValue(transaction.occurredAt, timeZone).replace('T', ' ')}
          </p>
        </div>
        <span className="badge badge--review">{confidence}% sure</span>
      </div>

      <p className="tiny faint" style={{ marginTop: 'var(--space-2)' }}>
        {sourceLabel(transaction.source)}
        {uncertain.length > 0 ? ` · check the ${uncertain.join(' and ')}` : ''}
      </p>

      {editing ? (
        <div className="stack" style={{ marginTop: 'var(--space-4)' }}>
          {error ? <Notice tone="danger">{error}</Notice> : null}

          <Field label="Amount">
            {(props) => (
              <TextInput
                {...props}
                inputMode="decimal"
                value={amountText}
                onChange={(event) => setAmountText(event.target.value.replace(/[^0-9.,]/g, ''))}
              />
            )}
          </Field>

          <Field label="Merchant">
            {(props) => (
              <TextInput
                {...props}
                value={merchant}
                autoCapitalize="words"
                onChange={(event) => setMerchant(event.target.value)}
              />
            )}
          </Field>

          <Field label="Category">
            {(props) => (
              <Select
                {...props}
                value={categoryId}
                onChange={(event) => setCategoryId(event.target.value)}
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.icon} {category.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Paid with">
            {(props) => (
              <Select
                {...props}
                value={accountLabel}
                onChange={(event) => setAccountLabel(event.target.value)}
              >
                {[...new Set([accountLabel, ...accounts.map((account) => account.label)])]
                  .filter(Boolean)
                  .map((label) => (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  ))}
              </Select>
            )}
          </Field>

          <Field label="Date and time">
            {(props) => (
              <TextInput
                {...props}
                type="datetime-local"
                value={occurredAtLocal}
                onChange={(event) => setOccurredAtLocal(event.target.value)}
              />
            )}
          </Field>

          <div className="row">
            <Button variant="quiet" className="grow" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button variant="accent" className="grow" onClick={() => handleConfirm(true)}>
              Save and confirm
            </Button>
          </div>
        </div>
      ) : (
        <div className="row" style={{ marginTop: 'var(--space-4)' }}>
          <Button variant="accent" className="grow" onClick={() => handleConfirm(false)}>
            Looks right
          </Button>
          <Button variant="secondary" className="grow" onClick={() => setEditing(true)}>
            Fix
          </Button>
          <Button
            variant="quiet"
            onClick={async () => {
              await deleteTransaction(transaction.id);
              onDeleted(transaction.id);
            }}
            aria-label="Delete this record"
          >
            Delete
          </Button>
        </div>
      )}
    </section>
  );
}
